import React, { useState } from 'react';
import { Archive, LogOut, MessageSquare, Monitor, MoonStar, Palette, SunMedium, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deleteAccountApi, deleteAllConversationsApi, logoutAllDevicesApi } from '../../api/memory.api';
import { LanguageSwitcher } from '../../components/ui/LanguageSwitcher';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { logout } from '../../redux/slices/authSlice';
import { setTheme } from '../../redux/slices/appSlice';
import { classNames } from '../../utils/classNames';
import { notifyRecentActivityReload, notifyRecentActivityRemoved } from '../activity/recentActivityEvents';
import { ArchivedChatsDialog } from './ArchivedChatsDialog';
import { ConfirmDangerDialog } from './ConfirmDangerDialog';
import { useSettingsModal } from './SettingsModalContext';
import { SettingsActionButton, SettingsRow, SettingsSection, settingsControlClass, ThemeSwatch } from './settingsUi';

export const GeneralSettingsPanel = () => {
  const { t } = useI18n();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { closeSettings } = useSettingsModal();
  const theme = useAppSelector((state) => state.app.theme);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const themeOptions = [
    { value: 'system', label: t('systemTheme'), icon: Monitor },
    { value: 'light', label: t('light'), icon: SunMedium },
    { value: 'dark', label: t('dark'), icon: MoonStar }
  ];

  const runDanger = async (action) => {
    setBusy(true);
    try {
      await action();
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <SettingsSection icon={Palette} title={t('settingsAppearanceSection')} description={t('settingsPreferencesHint')}>
        <div className="px-4 py-4">
          <p className="text-sm font-medium text-text">{t('appTheme')}</p>
          <p className="mt-0.5 text-xs text-muted">{t('appThemeHint')}</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const active = theme === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => dispatch(setTheme(option.value))}
                  aria-pressed={active}
                  className={classNames(
                    settingsControlClass,
                    'flex flex-col gap-2 rounded-2xl border p-2 text-start',
                    active
                      ? 'border-accent bg-accent-soft/70 ring-2 ring-accent/25'
                      : 'border-border bg-white/60 hover:border-accent/35 dark:bg-slate-950/30'
                  )}
                >
                  <ThemeSwatch mode={option.value} />
                  <span className="inline-flex items-center gap-1.5 px-0.5 text-xs font-semibold text-text">
                    <Icon size={13} aria-hidden />
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <SettingsRow title={t('interfaceLanguage')} description={t('languageSettingsHint')}>
          <LanguageSwitcher />
        </SettingsRow>
      </SettingsSection>

      <SettingsSection icon={MessageSquare} title={t('settingsChatsSection')}>
        <SettingsRow title={t('archivedChatsTitle')} description={t('archivedChatsHint')}>
          <SettingsActionButton onClick={() => setArchivedOpen(true)}>
            <Archive size={14} aria-hidden />
            {t('manage')}
          </SettingsActionButton>
        </SettingsRow>
        <SettingsRow title={t('deleteAllChatsTitle')} description={t('deleteAllChatsHint')} danger>
          <SettingsActionButton danger onClick={() => setConfirm('chats')}>
            <Trash2 size={14} aria-hidden />
            {t('deleteAll')}
          </SettingsActionButton>
        </SettingsRow>
      </SettingsSection>

      <SettingsSection icon={LogOut} title={t('settingsDangerZone')} description={t('deleteAccountInfo')} danger>
        <SettingsRow title={t('logoutAllTitle')} description={t('logoutAllHint')} danger>
          <SettingsActionButton danger onClick={() => setConfirm('logout')}>
            {t('logoutAction')}
          </SettingsActionButton>
        </SettingsRow>
        <SettingsRow title={t('deleteAccountTitle')} description={t('deleteAccountHint')} info={t('deleteAccountInfo')} danger>
          <SettingsActionButton danger onClick={() => setConfirm('account')}>
            {t('deleteAccountAction')}
          </SettingsActionButton>
        </SettingsRow>
      </SettingsSection>

      <ArchivedChatsDialog open={archivedOpen} onClose={() => setArchivedOpen(false)} />

      <ConfirmDangerDialog
        open={confirm === 'chats'}
        title={t('deleteAllChatsTitle')}
        message={t('deleteAllChatsConfirm')}
        confirmLabel={busy ? t('deleting') : t('deleteAll')}
        loading={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          runDanger(async () => {
            await deleteAllConversationsApi();
            notifyRecentActivityReload();
            notifyRecentActivityRemoved({ type: 'conversation', all: true });
            closeSettings();
            navigate('/chat');
          })
        }
      />

      <ConfirmDangerDialog
        open={confirm === 'logout'}
        title={t('logoutAllTitle')}
        message={t('logoutAllConfirm')}
        confirmLabel={t('logoutAction')}
        loading={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          runDanger(async () => {
            await logoutAllDevicesApi();
            closeSettings();
            dispatch(logout());
            navigate('/login');
          })
        }
      />

      <ConfirmDangerDialog
        open={confirm === 'account'}
        title={t('deleteAccountTitle')}
        message={t('deleteAccountConfirm')}
        confirmLabel={t('deleteAccountAction')}
        loading={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          runDanger(async () => {
            await deleteAccountApi();
            closeSettings();
            dispatch(logout());
            navigate('/login');
          })
        }
      />
    </div>
  );
};
